import { Box, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import {
  border,
  buildDataTestId,
  buildDataTestIdAttribute,
  buildFullDataTestId,
  DATA_TEST_ID_SELECTION,
  replaceCebabCase,
} from '@src/utils';
import { Controller, useFormContext } from 'react-hook-form';
import { initialSalesRepFilter, visibilityTypes } from './Visibility.contst';
import {
  salesRepresentativesApiSlice,
  selectionsApiSlice,
  setAllScope,
  setProvidersScope,
  setVisibilityIsChanged,
  VisibilityTypes,
} from '@src/api';
import { InputHTMLAttributes, useCallback, useEffect, useMemo, useState } from 'react';
import { ForAll, ForMeAndSalesRep, ForOrderingProviders } from './SettingsTypes';
import { FormAutocompleteField } from '@components/FormFields';
import { useDebounce } from '@src/hooks/useDebounce';
import { visibilityDefaultValue } from '@pages/Selection/defaultValues';
import { FetchingContext } from './FetchingContext';
import { useParams } from 'react-router-dom';
import { IntersectionType } from '@src/api/types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@src/store';

type SalesRepFilterType = {
  search: string;
  skip: number;
  take: number;
};

const SALES_REP_FIELD_NAME = 'createVisibility.salesRepresentativeId';

const createScope = { isAllOrderingProviders: true, isAllLaboratories: true, salesRepresentative: null };

export const Visibility = ({
  isFetching,
  visibilityTypeDefault,
}: {
  isFetching: boolean;
  visibilityTypeDefault?: VisibilityTypes;
}) => {
  const { watch, control, setValue } = useFormContext();

  const scopeStore = useSelector((state: RootState) => state.selectionScope);

  const { selectionId } = useParams();
  const dispatch = useDispatch();

  const [visibilityType, salesRep] = watch(['visibilityType', SALES_REP_FIELD_NAME]);
  const [salesRepFilter, setSalesRepFilter] = useState<Nullable<SalesRepFilterType>>(null);

  const debouncedSalesRepFilter = useDebounce(salesRepFilter);

  const { data: scope } = selectionsApiSlice.useGetSelectionScopesQuery(selectionId || '', { skip: !selectionId });

  useEffect(() => {
    if (selectionId) {
      if (scope) {
        dispatch(setAllScope(scope));
      } else return;
    } else {
      dispatch(setAllScope(createScope));
    }
  }, [scope, dispatch, selectionId]);

  const { reps, isLoading, isEnd } = salesRepresentativesApiSlice.useGetLabsQuery(
    debouncedSalesRepFilter as SalesRepFilterType,
    {
      skip: !debouncedSalesRepFilter,
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, ...rest }) => {
        return {
          ...rest,
          isEnd: data?.isEnd,
          reps: [
            ...(data?.data.map(({ id, name }) => ({
              name,
              value: id,
            })) || []),
          ],
        };
      },
    },
  );

  const startSearch = () => {
    setSalesRepFilter(initialSalesRepFilter);
  };

  useEffect(() => {
    if (scope?.isAllLaboratories) {
      setValue('createVisibility.laboratoryIntersectionType', IntersectionType.Exclude);
      setValue('createVisibility.laboratoryIds', []);
    }

    if (scope?.isAllOrderingProviders) {
      setValue('createVisibility.facilityIntersectionType', IntersectionType.Exclude);
      setValue('createVisibility.facilityVisibilities', []);
    }
  }, [scope, setValue]);

  useEffect(() => {
    if (selectionId ? isFetching && visibilityType !== visibilityTypeDefault : true) {
      setValue('createVisibility', visibilityDefaultValue);
    }
  }, [visibilityType, setValue, isFetching, selectionId, visibilityTypeDefault, dispatch]);

  useEffect(() => {
    if (visibilityType && visibilityType !== VisibilityTypes.ForOneSalesRepresentative) {
      setValue(SALES_REP_FIELD_NAME, null);
    }
  }, [visibilityType, setValue]);

  useEffect(() => {
    setValue(SALES_REP_FIELD_NAME, scope?.salesRepresentative?.id || '');
  }, [scope?.salesRepresentative, setValue]);

  const renderVisibilitySettings = useMemo(() => {
    switch (visibilityType) {
      case VisibilityTypes.ForMe: {
        return <ForMeAndSalesRep />;
      }
      case VisibilityTypes.ForAll: {
        return <ForAll />;
      }
      case VisibilityTypes.ForOneSalesRepresentative: {
        return <ForMeAndSalesRep disabled={!salesRep} />;
      }
      case VisibilityTypes.ForOrderingProviders: {
        return <ForOrderingProviders />;
      }

      default: {
        return null;
      }
    }
  }, [visibilityType, salesRep]);

  const onSearchSalesRep = useCallback(e => {
    const value = e?.target?.value;

    if (value && value.length === 0) {
      setSalesRepFilter(prev => prev && { ...prev, skip: 0, search: '' });
    } else if (value && value.length >= 2) {
      setSalesRepFilter(prev => prev && { ...prev, skip: 0, search: value });
    }
  }, []);

  const onChangeSalesRep = () => {
    if (dispatch) {
      setValue('createVisibility.facilityIntersectionType', IntersectionType.Exclude);
      setValue('createVisibility.facilityVisibilities', []);
      dispatch(setProvidersScope(true));
    }
  };

  const dataTestId = buildFullDataTestId(DATA_TEST_ID_SELECTION, 'visibility');

  return (
    <Stack gap="24px" padding="36px" borderBottom={border}>
      <Typography color="grey.700" variant="head24Medium" lineHeight="34px">
        Sharing settings
      </Typography>
      <Stack direction="row" alignItems="center">
        <Typography component="p" width="120px" color="#4B5563" variant="label14Bold">
          Selection for
        </Typography>
        <Controller
          name="visibilityType"
          control={control}
          render={({ field: { value, onChange } }) => {
            const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
              onChange((event.target as HTMLInputElement).value);
              dispatch(setAllScope(createScope));
              dispatch(setVisibilityIsChanged());
            };
            return (
              <RadioGroup
                value={value}
                onChange={handleChange}
                sx={{ flexDirection: 'row', gap: '24px', width: '100%' }}
              >
                {visibilityTypes.map(({ name, value: visibilityValue }) => {
                  const isSalesRep = visibilityValue === VisibilityTypes.ForOneSalesRepresentative;
                  return (
                    <FormControlLabel
                      key={visibilityValue}
                      value={visibilityValue}
                      sx={{
                        gap: '12px',
                        flexGrow: isSalesRep ? 1 : 0,
                        '& .MuiFormControlLabel-label': {
                          flexGrow: isSalesRep ? 1 : 0,
                        },
                      }}
                      control={
                        <Radio
                          size="small"
                          inputProps={
                            {
                              ...buildDataTestIdAttribute(
                                dataTestId,
                                `radio-input-${replaceCebabCase(value as string)}`,
                              ),
                            } as InputHTMLAttributes<HTMLInputElement>
                          }
                        />
                      }
                      label={
                        isSalesRep ? (
                          <Box width="100%">
                            <FormAutocompleteField
                              name="createVisibility.salesRepresentativeId"
                              control={control}
                              placeholder="Sales representative"
                              options={reps || []}
                              disabled={value !== VisibilityTypes.ForOneSalesRepresentative}
                              onOpen={startSearch}
                              onSearch={onSearchSalesRep}
                              onChange={onChangeSalesRep}
                              loading={isLoading}
                              isEnd={isEnd}
                              setValuesFilter={setSalesRepFilter}
                              withoutLocalValue={value !== VisibilityTypes.ForOneSalesRepresentative}
                              editValue={
                                value === VisibilityTypes.ForOneSalesRepresentative
                                  ? scopeStore?.salesRepresentative || null
                                  : null
                              }
                              dataTestId={buildDataTestId(dataTestId, 'input-sales-rep')}
                              disableClearIcon
                            />
                          </Box>
                        ) : (
                          <Typography color="grey.700" variant="label14Medium" lineHeight="">
                            {name}
                          </Typography>
                        )
                      }
                    />
                  );
                })}
              </RadioGroup>
            );
          }}
        />
      </Stack>
      <Stack direction="row" alignItems="center">
        {(visibilityType === VisibilityTypes.ForMe || visibilityType === VisibilityTypes.ForOneSalesRepresentative) && (
          <Typography component="p" width="120px" color="#4B5563" variant="label14Bold">
            Accounts
          </Typography>
        )}
        <FetchingContext.Provider value={{ isFetching }}>{renderVisibilitySettings}</FetchingContext.Provider>
      </Stack>
    </Stack>
  );
};
